/**
 * Beta Life Events content pack.
 *
 * Premium "real-world interruption" scenarios that appear between beta path
 * lessons and modules. Beta-only. Options are stored in canonical order with
 * a stable `correctOptionId` so the overlay can shuffle on every attempt
 * without breaking correctness mapping.
 *
 * IMPORTANT: never use option array index as correctness — always grade
 * against `correctOptionId`.
 */
import { launchLessonIds, type BetaModuleTitle } from "@/lib/launch/launchExperienceRoster";

export type LifeEventOutcome = "positive" | "negative";

export interface LaunchLifeEventOption {
  id: string;
  text: string;
  outcomeType: LifeEventOutcome;
}

export type BetaLifeEventAnchor =
  | { kind: "lesson"; lessonId: string }
  | { kind: "module"; title: BetaModuleTitle };

export interface BetaLifeEventSfx {
  appear: string;
  select: string;
  confirm: string;
  correct: string;
  incorrect: string;
  moneyGain: string;
  moneyLoss: string;
}

export interface LaunchLifeEvent {
  id: string;
  anchor: BetaLifeEventAnchor;
  title: string;
  scenario: string;
  question: string;
  options: LaunchLifeEventOption[];
  correctOptionId: string;
  explanation: string;
  walletImpactCorrect: number;
  walletImpactIncorrect: number;
  skillGained: string;
  whyItMattered: string;
  sfx: BetaLifeEventSfx;
}

const DEFAULT_SFX: BetaLifeEventSfx = {
  appear: "warning_risk",
  select: "decision_select",
  confirm: "decision_lock",
  correct: "correct_soft",
  incorrect: "incorrect_soft",
  moneyGain: "money_gain",
  moneyLoss: "money_loss",
};

// Lesson IDs in beta roster order:
// 0 Needs vs Wants, 1 Opportunity Cost, 2 Financial Tradeoffs, 3 Delayed Gratification,
// 4 Cash Flow, 5 Credit Score Basics, 6 Borrowing Money, 7 Minimum Payments Trap,
// 8 Urgency as a Red Flag, 9 Too-Good-To-Be-True Returns
const LID = launchLessonIds;

export const betaLifeEvents: LaunchLifeEvent[] = [
  {
    id: "life_event_needs_weekend_drop",
    anchor: { kind: "lesson", lessonId: LID[0] },
    title: "The Weekend Drop",
    scenario:
      "A limited-edition hoodie drops this weekend for $85. You have $110 in your account, but you also need $40 for lunch money and $30 for a school club fee next week.",
    question: "What is the strongest financial decision?",
    options: [
      { id: "a", text: "Buy the hoodie now because it may sell out.", outcomeType: "negative" },
      { id: "b", text: "Set aside the lunch and club money first, then decide whether the hoodie still fits your budget.", outcomeType: "positive" },
      { id: "c", text: "Ask a friend to cover your club fee so you can buy the hoodie.", outcomeType: "negative" },
      { id: "d", text: "Spend the full $110 and figure out next week later.", outcomeType: "negative" },
    ],
    correctOptionId: "b",
    explanation:
      "Needs and commitments come before wants. The hoodie may be exciting, but lunch money and the club fee are upcoming obligations.",
    walletImpactCorrect: 15,
    walletImpactIncorrect: -30,
    skillGained: "Prioritizing needs over wants",
    whyItMattered: "Protecting upcoming obligations keeps your cash flow stable instead of stressed.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_opportunity_one_saturday",
    anchor: { kind: "lesson", lessonId: LID[1] },
    title: "One Saturday, Two Choices",
    scenario:
      "You can work a 4-hour shift and earn $64, or go to a paid gaming tournament that costs $20 to enter. You cannot do both.",
    question: "What should you consider before choosing?",
    options: [
      { id: "a", text: "Only whether your friends are going.", outcomeType: "negative" },
      { id: "b", text: "Whether the tournament has cool prizes.", outcomeType: "negative" },
      { id: "c", text: "What you give up by choosing one option over the other.", outcomeType: "positive" },
      { id: "d", text: "Which option sounds more fun in the moment.", outcomeType: "negative" },
    ],
    correctOptionId: "c",
    explanation:
      "Opportunity cost is the value of the next best option you give up. The real decision is not just 'fun vs work'; it is what each choice costs you.",
    walletImpactCorrect: 10,
    walletImpactIncorrect: -20,
    skillGained: "Opportunity cost thinking",
    whyItMattered: "Naming what you're giving up turns a vibe decision into a real one.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_tradeoffs_upgrade_or_build",
    anchor: { kind: "lesson", lessonId: LID[2] },
    title: "Upgrade or Build?",
    scenario:
      "You have saved $300. You want a new phone upgrade, but your current phone works. You also want to buy equipment to start a small weekend lawn-care service.",
    question: "Which choice shows the strongest tradeoff thinking?",
    options: [
      { id: "a", text: "Buy the phone because new technology is always worth it.", outcomeType: "negative" },
      { id: "b", text: "Spend half on snacks and half on the business equipment.", outcomeType: "negative" },
      { id: "c", text: "Ignore both options and leave all the money untouched forever.", outcomeType: "negative" },
      { id: "d", text: "Compare which option creates more long-term value before spending.", outcomeType: "positive" },
    ],
    correctOptionId: "d",
    explanation:
      "Tradeoff thinking means comparing what each choice gives you and what it costs. A working phone may be less valuable than equipment that could help you earn.",
    walletImpactCorrect: 25,
    walletImpactIncorrect: -75,
    skillGained: "Long-term value comparison",
    whyItMattered: "Some purchases earn money back. Others just spend it.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_delayed_buy_now_or_wait",
    anchor: { kind: "lesson", lessonId: LID[3] },
    title: "Buy Now or Wait?",
    scenario:
      "A pair of headphones you want costs $120 today. You know they usually drop to $90 during a sale next month. You have enough money now, but buying today would empty most of your account.",
    question: "What is the best example of delayed gratification?",
    options: [
      { id: "a", text: "Wait for the sale and keep more cash available.", outcomeType: "positive" },
      { id: "b", text: "Buy them now because waiting is annoying.", outcomeType: "negative" },
      { id: "c", text: "Borrow money from someone so your account balance does not drop.", outcomeType: "negative" },
      { id: "d", text: "Buy a cheaper random item today just to feel like you bought something.", outcomeType: "negative" },
    ],
    correctOptionId: "a",
    explanation:
      "Delayed gratification means waiting for a better outcome instead of chasing the immediate reward. Waiting saves money and keeps your account healthier.",
    walletImpactCorrect: 30,
    walletImpactIncorrect: -30,
    skillGained: "Patience-based decision making",
    whyItMattered: "A month of patience often beats a moment of impulse.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_cashflow_180_paycheck",
    anchor: { kind: "lesson", lessonId: LID[4] },
    title: "The $180 Paycheck",
    scenario:
      "You earned $180 this week. You owe $45 for a school trip deposit, want to save $40, and need about $30 for gas or rides.",
    question: "What is the smartest first move?",
    options: [
      { id: "a", text: "Spend first, then save whatever is left.", outcomeType: "negative" },
      { id: "b", text: "Put the full $180 into savings and ignore the trip deposit.", outcomeType: "negative" },
      { id: "c", text: "Plan the money before spending by covering obligations, savings, and expected costs.", outcomeType: "positive" },
      { id: "d", text: "Use the money for wants because another paycheck may come later.", outcomeType: "negative" },
    ],
    correctOptionId: "c",
    explanation:
      "Cash flow means knowing what money is coming in and where it needs to go. Planning first prevents shortfalls later.",
    walletImpactCorrect: 20,
    walletImpactIncorrect: -45,
    skillGained: "Cash flow planning",
    whyItMattered: "A simple plan beats a perfect intention.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_credit_first_offer",
    anchor: { kind: "lesson", lessonId: LID[5] },
    title: "First Credit Card Offer",
    scenario:
      "You receive your first credit card offer with a $500 limit. You are thinking about using it for everyday purchases.",
    question: "Which behavior would help your credit score the most?",
    options: [
      { id: "a", text: "Max out the card because using more credit builds credit faster.", outcomeType: "negative" },
      { id: "b", text: "Open the card and never check the balance.", outcomeType: "negative" },
      { id: "c", text: "Pay late sometimes so the lender knows you are active.", outcomeType: "negative" },
      { id: "d", text: "Keep the balance low and pay on time every month.", outcomeType: "positive" },
    ],
    correctOptionId: "d",
    explanation:
      "Credit scores are heavily influenced by payment history and credit utilization. Low balances and on-time payments are strong credit habits.",
    walletImpactCorrect: 20,
    walletImpactIncorrect: -50,
    skillGained: "Credit-building habits",
    whyItMattered: "Two boring habits — low balances and on-time payments — build most of your score.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_borrowing_laptop_loan",
    anchor: { kind: "lesson", lessonId: LID[6] },
    title: "Laptop Loan",
    scenario:
      'You need a laptop for school. One option is $600 paid upfront. Another is "only $35/month," but the total cost after fees is $840.',
    question: "What should you compare before borrowing?",
    options: [
      { id: "a", text: "Only the monthly payment, because smaller payments are always better.", outcomeType: "negative" },
      { id: "b", text: "The total repayment amount, fees, interest, and whether the payment fits your cash flow.", outcomeType: "positive" },
      { id: "c", text: "Whether the seller says most students choose the monthly plan.", outcomeType: "negative" },
      { id: "d", text: "Whether the loan approval feels quick and easy.", outcomeType: "negative" },
    ],
    correctOptionId: "b",
    explanation:
      "Borrowing should be judged by total cost and repayment ability, not just the monthly payment. A low payment can hide a much higher total cost.",
    walletImpactCorrect: 40,
    walletImpactIncorrect: -60,
    skillGained: "Total-cost borrowing analysis",
    whyItMattered: "Small payments can hide big totals. Always check the full price.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_min_payments_420_balance",
    anchor: { kind: "lesson", lessonId: LID[7] },
    title: "The $420 Balance",
    scenario:
      "You have a $420 credit card balance. The minimum payment is $18, but interest keeps adding up. You have $90 available after covering your needs.",
    question: "What is the best move?",
    options: [
      { id: "a", text: "Pay only the minimum because that is all the company requires.", outcomeType: "negative" },
      { id: "b", text: "Skip the payment and pay more next month.", outcomeType: "negative" },
      { id: "c", text: "Pay more than the minimum while still keeping enough cash for necessities.", outcomeType: "positive" },
      { id: "d", text: "Open a second card to pay the first card.", outcomeType: "negative" },
    ],
    correctOptionId: "c",
    explanation:
      "Minimum payments keep the account current, but they can stretch debt out and increase interest. Paying more than the minimum reduces the trap.",
    walletImpactCorrect: 25,
    walletImpactIncorrect: -40,
    skillGained: "Escaping the minimum payments trap",
    whyItMattered: "Paying just the minimum is how a small debt becomes a long one.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_urgency_irs_call",
    anchor: { kind: "lesson", lessonId: LID[8] },
    title: "The IRS Call",
    scenario:
      "Someone calls claiming to be from the IRS. They say you must pay within 10 minutes using gift cards or your family will be in legal trouble.",
    question: "What is the safest response?",
    options: [
      { id: "a", text: "Stay on the phone and follow instructions quickly.", outcomeType: "negative" },
      { id: "b", text: "Give partial payment to buy more time.", outcomeType: "negative" },
      { id: "c", text: "Hang up, do not pay, and verify through an official source or trusted adult.", outcomeType: "positive" },
      { id: "d", text: "Ask the caller which gift card brand they prefer.", outcomeType: "negative" },
    ],
    correctOptionId: "c",
    explanation:
      "Extreme urgency and gift card payment demands are major scam signals. Safe decisions slow the situation down and verify independently.",
    walletImpactCorrect: 50,
    walletImpactIncorrect: -200,
    skillGained: "Scam urgency defense",
    whyItMattered: "Real agencies don't demand gift cards in 10 minutes. Urgency is the scam.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_too_good_guaranteed_returns",
    anchor: { kind: "lesson", lessonId: LID[9] },
    title: "Guaranteed Returns DM",
    scenario:
      'Someone messages you saying they can turn $100 into $1,000 in one week with "zero risk." Their page shows screenshots of huge profits.',
    question: "What is the best decision?",
    options: [
      { id: "a", text: "Send $100 because the screenshots prove it works.", outcomeType: "negative" },
      { id: "b", text: "Ask if they can make it $2,000 instead.", outcomeType: "negative" },
      { id: "c", text: "Share your login so they can invest faster.", outcomeType: "negative" },
      { id: "d", text: "Do not send money or personal info; treat guaranteed high returns as a major red flag.", outcomeType: "positive" },
    ],
    correctOptionId: "d",
    explanation:
      "High returns with no risk are not realistic. Scammers often use fake proof, urgency, and social pressure to make the opportunity look real.",
    walletImpactCorrect: 30,
    walletImpactIncorrect: -100,
    skillGained: "Spotting fraud bait",
    whyItMattered: "Guaranteed high returns are not a strategy. They are a warning sign.",
    sfx: DEFAULT_SFX,
  },
  // Module life events
  {
    id: "life_event_budget_tire_surprise",
    anchor: { kind: "module", title: "Budget Builder Module" },
    title: "The Tire Surprise",
    scenario:
      "Your family says you need to help cover a $75 tire repair because you use the car for school and work. You did not plan for it.",
    question: "What is the strongest budget response?",
    options: [
      { id: "a", text: "Ignore it because it was not in the original budget.", outcomeType: "negative" },
      { id: "b", text: "Use the emergency/sinking fund if available, then adjust flexible spending for the week.", outcomeType: "positive" },
      { id: "c", text: "Put it on a high-interest loan immediately.", outcomeType: "negative" },
      { id: "d", text: "Stop tracking your budget because surprises make budgeting useless.", outcomeType: "negative" },
    ],
    correctOptionId: "b",
    explanation:
      "A good budget is not perfect; it adapts. Surprise expenses are exactly why emergency funds and flexible categories matter.",
    walletImpactCorrect: 20,
    walletImpactIncorrect: -75,
    skillGained: "Budget resilience",
    whyItMattered: "Budgets that bend don't break.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_credit_statement_date",
    anchor: { kind: "module", title: "Credit Management Module" },
    title: "Statement Date Strategy",
    scenario:
      "Your card limit is $500. You spent $210 this month, but you can pay $150 before the statement closes.",
    question: "Why might paying before the statement date help?",
    options: [
      { id: "a", text: "It may lower reported utilization before the balance is reported.", outcomeType: "positive" },
      { id: "b", text: "It guarantees the credit card company will raise your limit.", outcomeType: "negative" },
      { id: "c", text: "It removes the need to pay the bill later.", outcomeType: "negative" },
      { id: "d", text: "It makes interest disappear forever.", outcomeType: "negative" },
    ],
    correctOptionId: "a",
    explanation:
      "Paying before the statement closes can reduce the balance reported to credit bureaus, which may help utilization. You still need to manage the remaining bill responsibly.",
    walletImpactCorrect: 20,
    walletImpactIncorrect: -30,
    skillGained: "Utilization timing",
    whyItMattered: "When you pay can matter as much as how much you pay.",
    sfx: DEFAULT_SFX,
  },
  {
    id: "life_event_scam_fake_login",
    anchor: { kind: "module", title: "Scam Defense Module" },
    title: "Fake Login Page",
    scenario:
      "You receive a text saying your bank account is locked. The link opens a page that looks real, but the URL is slightly misspelled.",
    question: "What should you do?",
    options: [
      { id: "a", text: "Type your password quickly to unlock the account.", outcomeType: "negative" },
      { id: "b", text: 'Click "forgot password" on the suspicious page.', outcomeType: "negative" },
      { id: "c", text: "Close the link, go to the bank app or official website directly, and report the message.", outcomeType: "positive" },
      { id: "d", text: "Forward the link to friends to ask if it looks real.", outcomeType: "negative" },
    ],
    correctOptionId: "c",
    explanation:
      "Fake login pages are designed to steal credentials. The safest move is to avoid the link and access the account through a trusted route.",
    walletImpactCorrect: 40,
    walletImpactIncorrect: -120,
    skillGained: "Phishing defense",
    whyItMattered: "Check the URL. Go direct. Never trust the link in the text.",
    sfx: DEFAULT_SFX,
  },
];

export function getLifeEventForLesson(lessonId: string): LaunchLifeEvent | null {
  return (
    betaLifeEvents.find(
      (e) => e.anchor.kind === "lesson" && e.anchor.lessonId === lessonId,
    ) ?? null
  );
}

export function getLifeEventForModule(title: string): LaunchLifeEvent | null {
  return (
    betaLifeEvents.find(
      (e) => e.anchor.kind === "module" && e.anchor.title === title,
    ) ?? null
  );
}

/** Fisher–Yates shuffle that preserves `correctOptionId` mapping. */
export function shuffleLifeEventOptions(event: LaunchLifeEvent): LaunchLifeEventOption[] {
  const copy = [...event.options];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const SEEN_KEY = "heirwise:beta-life-events-seen";

export function hasSeenLifeEvent(eventId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (!raw) return false;
    const ids = JSON.parse(raw) as string[];
    return Array.isArray(ids) && ids.includes(eventId);
  } catch {
    return false;
  }
}

export function markLifeEventSeen(eventId: string): void {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    const ids = raw ? (JSON.parse(raw) as string[]) : [];
    if (!ids.includes(eventId)) ids.push(eventId);
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
  } catch {
    /* best-effort */
  }
}